import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/calendar-view-dialog.css';

const CalendarViewDialog = ({ entry, onEdit, onDelete, onClose }) => {
  // const { t } = useLanguage(); // TODO: Add localization
  const [playingAudio, setPlayingAudio] = useState(null);
  
  // Add CSS keyframe animation for smooth entrance
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes dialogSlideIn {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  // Handle audio playback
  const handleAudioPlay = async (audioClip) => {
    if (playingAudio) {
      playingAudio.pause();
      setPlayingAudio(null);
    }
    
    try {
      const audio = new Audio(audioClip.file_url);
      audio.onended = () => setPlayingAudio(null);
      audio.onerror = () => {
        console.error('Failed to play audio:', audioClip.filename);
        setPlayingAudio(null);
      };
      
      await audio.play();
      setPlayingAudio(audio);
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };
  
  // Stop audio playback
  const handleAudioStop = () => {
    if (playingAudio) {
      playingAudio.pause();
      setPlayingAudio(null);
    }
  };

  // Handle audio download
  const handleAudioDownload = async (clip) => {
    try {
      const response = await fetch(clip.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = clip.filename || clip.name || 'audio-clip.webm';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download audio:', error);
      // Fallback to opening in new tab
      window.open(clip.file_url, '_blank');
    }
  };
  
  // Get entry type display name
  const getEntryTypeDisplay = (type) => {
    switch (type) {
      case 'rehearsal_notes':
        return 'Rehearsal Notes';
      case 'performance':
        return 'Performance';
      case 'notice':
        return 'General Notice';
      case 'rehearsal_info':
        return 'Rehearsal Information';
      default:
        return type;
    }
  };
  
  // Get entry type icon
  const getEntryTypeIcon = (entry) => {
    // Use custom icon if available, otherwise fall back to type-based icon
    if (entry.icon) {
      return entry.icon;
    }
    
    switch (entry.entry_type) {
      case 'rehearsal_notes':
        return '📝';
      case 'performance':
        return '🎭';
      case 'notice':
        return '📢';
      case 'rehearsal_info':
        return '🎵';
      default:
        return '📅';
    }
  };
  
  return (
    <div 
      className="dialog-overlay calendar-view-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        backdropFilter: 'blur(12px)',
        padding: '20px'
      }}
    >
      <div 
        className="calendar-view-dialog" 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '20px',
          boxShadow: `
            0 32px 64px rgba(0, 0, 0, 0.8),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.2),
            inset 0 -1px 0 rgba(0, 0, 0, 0.8)
          `,
          maxWidth: '900px',
          width: '95vw',
          maxHeight: 'calc(100vh - 40px)',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          animation: 'dialogSlideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        }}
      >
        <div 
          className="dialog-header"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '32px',
            background: 'linear-gradient(135deg, #2a2a2a 0%, #363636 50%, #2a2a2a 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            flexShrink: 0,
            position: 'relative'
          }}
        >
          <div 
            className="entry-header"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '20px',
              flex: 1
            }}
          >
            <span 
              className="entry-icon"
              style={{
                fontSize: '48px',
                lineHeight: '1',
                marginTop: '2px',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))',
                transform: 'scale(1)',
                transition: 'transform 0.2s ease'
              }}
            >
              {getEntryTypeIcon(entry)}
            </span>
            <div className="entry-meta">
              <h3 
                className="entry-title"
                style={{
                  color: '#ffffff',
                  margin: '0 0 12px 0',
                  fontSize: '26px',
                  fontWeight: '700',
                  lineHeight: '1.2',
                  letterSpacing: '-0.03em',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {entry.title}
              </h3>
              <div 
                className="entry-info"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <span 
                  className="entry-type"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'inline-block',
                    width: 'fit-content',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  {getEntryTypeDisplay(entry.entry_type)}
                </span>
                <span 
                  className="entry-date"
                  style={{
                    color: '#b8b8b8',
                    fontSize: '15px',
                    fontWeight: '500',
                    letterSpacing: '0.3px'
                  }}
                >
                  {formatDate(entry.date)}
                </span>
              </div>
            </div>
          </div>
          
          <div 
            className="dialog-header-actions"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            {onEdit && (
              <button 
                className="edit-button"
                onClick={() => onEdit(entry)}
                title={entry.entry_type === 'rehearsal_info' && entry.associatedNotes 
                  ? 'Edit rehearsal notes' 
                  : 'Edit entry'}
                style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0px)'}
              >
                ✏️ {entry.entry_type === 'rehearsal_info' && entry.associatedNotes 
                  ? 'Edit Notes' 
                  : 'Edit'}
              </button>
            )}
            
            {onDelete && (entry.entry_type !== 'rehearsal_info' || (entry.entry_type === 'rehearsal_info' && entry.associatedNotes)) && (
              <button 
                className="delete-button"
                onClick={() => onDelete(entry)}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0px)'}
              >
                🗑️ {entry.entry_type === 'rehearsal_info' && entry.associatedNotes 
                  ? 'Delete Notes' 
                  : 'Delete'}
              </button>
            )}
            
            <button 
              className="close-button" 
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '12px',
                borderRadius: '10px',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '44px',
                height: '44px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0px)';
              }}
            >
              ✕
            </button>
          </div>
        </div>
        
        <div 
          className="dialog-content"
          style={{
            padding: '40px',
            flex: 1,
            overflowY: 'auto',
            color: '#e0e0e0',
            lineHeight: '1.6',
            fontSize: '15px',
            background: 'linear-gradient(180deg, rgba(26, 26, 26, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Rehearsal info with associated notes */}
          {entry.entry_type === 'rehearsal_info' && entry.associatedNotes && (
            <div 
              className="rehearsal-notes-section"
              style={{ marginBottom: '32px' }}
            >
              <h4 
                style={{
                  color: '#ffffff',
                  margin: '0 0 20px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                📝 Rehearsal Notes
              </h4>
              <div 
                className="notes-preview"
                style={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.12) 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <div 
                  className="notes-title"
                  style={{
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {entry.associatedNotes.title}
                </div>
                {entry.associatedNotes.content && (
                  <div 
                    className="notes-content"
                    style={{
                      color: '#e0e0e0',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      marginBottom: '16px',
                      whiteSpace: 'pre-wrap',
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    {entry.associatedNotes.content.substring(0, 200)}
                    {entry.associatedNotes.content.length > 200 ? '...' : ''}
                  </div>
                )}
                <div 
                  className="notes-action"
                  style={{
                    color: '#b0b0b0',
                    fontStyle: 'italic',
                    fontSize: '13px'
                  }}
                >
                  <small>Click "Edit Notes" above to view or edit the full notes.</small>
                </div>
              </div>
            </div>
          )}
          
          {/* Performance details */}
          {entry.entry_type === 'performance' && (
            <div 
              className="performance-details"
              style={{ marginBottom: '32px' }}
            >
              <h4 
                style={{
                  color: '#ffffff',
                  margin: '0 0 20px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                🎭 Performance Details
              </h4>
              
              <div 
                className="detail-grid"
                style={{
                  display: 'grid',
                  gap: '16px'
                }}
              >
                {entry.performance_time && (
                  <div 
                    className="detail-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <span 
                      className="detail-label"
                      style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        fontWeight: '600',
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      🕐 Time
                    </span>
                    <span 
                      className="detail-value"
                      style={{
                        color: '#ffffff',
                        fontSize: '15px',
                        fontWeight: '500',
                        flex: 1
                      }}
                    >
                      {formatTime(entry.performance_time)}
                    </span>
                  </div>
                )}
                
                {entry.venue_name && (
                  <div 
                    className="detail-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <span 
                      className="detail-label"
                      style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        fontWeight: '600',
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      🏛️ Venue
                    </span>
                    <span 
                      className="detail-value"
                      style={{
                        color: '#ffffff',
                        fontSize: '15px',
                        fontWeight: '500',
                        flex: 1
                      }}
                    >
                      {entry.venue_name}
                    </span>
                  </div>
                )}
                
                {entry.venue_address && (
                  <div 
                    className="detail-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <span 
                      className="detail-label"
                      style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        fontWeight: '600',
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      📍 Address
                    </span>
                    <span 
                      className="detail-value"
                      style={{
                        color: '#ffffff',
                        fontSize: '15px',
                        fontWeight: '500',
                        flex: 1
                      }}
                    >
                      {entry.venue_address}
                    </span>
                  </div>
                )}
                
                {entry.venue_maps_url && (
                  <div 
                    className="detail-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <span 
                      className="detail-label"
                      style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        fontWeight: '600',
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      🗺️ Directions
                    </span>
                    <a 
                      href={entry.venue_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="maps-link"
                      style={{
                        color: '#667eea',
                        textDecoration: 'none',
                        fontWeight: '500',
                        fontSize: '15px',
                        transition: 'color 0.2s ease',
                        flex: 1
                      }}
                      onMouseOver={(e) => e.target.style.color = '#7a8bff'}
                      onMouseOut={(e) => e.target.style.color = '#667eea'}
                    >
                      Open in Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Content */}
          {(entry.content_html || entry.content) && (
            <div 
              className="entry-content"
              style={{ marginBottom: '32px' }}
            >
              <h4 
                style={{
                  color: '#ffffff',
                  margin: '0 0 20px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                📄 Content
              </h4>
              {entry.content_html ? (
                <div 
                  className="content-html"
                  style={{
                    background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '24px',
                    lineHeight: '1.7',
                    fontSize: '15px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    color: '#e0e0e0'
                  }}
                  dangerouslySetInnerHTML={{ __html: entry.content_html }}
                />
              ) : (
                <div 
                  className="content-text"
                  style={{
                    background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '24px',
                    lineHeight: '1.7',
                    fontSize: '15px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    color: '#e0e0e0',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {entry.content}
                </div>
              )}
            </div>
          )}
          
          {/* Audio clips */}
          {entry.audio_clips && entry.audio_clips.length > 0 && (
            <div 
              className="audio-clips-section"
              style={{ marginBottom: '32px' }}
            >
              <h4 
                style={{
                  color: '#ffffff',
                  margin: '0 0 20px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                🎵 Audio Clips
              </h4>
              <div 
                className="audio-clips-list"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}
              >
                {entry.audio_clips.map(clip => (
                  <div 
                    key={clip.id} 
                    className="audio-clip-card"
                    style={{
                      background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(35, 35, 35, 0.9) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '20px',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div 
                      className="clip-info"
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}
                    >
                      <span 
                        className="clip-title"
                        style={{
                          color: '#ffffff',
                          fontSize: '16px',
                          fontWeight: '600',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        {clip.name || clip.title}
                      </span>
                      {clip.duration && (
                        <span 
                          className="clip-duration"
                          style={{
                            color: '#b0b0b0',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          Duration: {Math.round(clip.duration)}s
                        </span>
                      )}
                    </div>
                    
                    <div 
                      className="clip-controls"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      {playingAudio ? (
                        <button 
                          className="audio-button stop"
                          onClick={handleAudioStop}
                          style={{
                            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                            border: 'none',
                            color: 'white',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                          onMouseOut={(e) => e.target.style.transform = 'translateY(0px)'}
                        >
                          ⏹️ Stop
                        </button>
                      ) : (
                        <button 
                          className="audio-button play"
                          onClick={() => handleAudioPlay(clip)}
                          style={{
                            background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                            border: 'none',
                            color: 'white',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)'
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                          onMouseOut={(e) => e.target.style.transform = 'translateY(0px)'}
                        >
                          ▶️ Play
                        </button>
                      )}
                      
                      {clip.file_url && (
                        <button 
                          onClick={() => handleAudioDownload(clip)}
                          className="download-link"
                          style={{
                            background: 'transparent',
                            color: '#667eea',
                            fontSize: '14px',
                            fontWeight: '600',
                            padding: '10px 16px',
                            border: '1px solid #667eea',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.color = '#7a8bff';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.transform = 'translateY(0px)';
                            e.target.style.color = '#667eea';
                          }}
                        >
                          ⬇️ Download
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Metadata */}
          <div 
            className="entry-metadata"
            style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              paddingTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%)',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'
            }}
          >
            <div 
              className="metadata-item"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '13px',
                padding: '8px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}
            >
              <span 
                className="metadata-label"
                style={{
                  color: '#888',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Created
              </span>
              <span 
                className="metadata-value"
                style={{
                  color: '#b0b0b0',
                  fontWeight: '500'
                }}
              >
                {new Date(entry.created_at).toLocaleString()}
              </span>
            </div>
            
            {entry.updated_at && entry.updated_at !== entry.created_at && (
              <div 
                className="metadata-item"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '13px',
                  padding: '8px 0'
                }}
              >
                <span 
                  className="metadata-label"
                  style={{
                    color: '#888',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Last Updated
                </span>
                <span 
                  className="metadata-value"
                  style={{
                    color: '#b0b0b0',
                    fontWeight: '500'
                  }}
                >
                  {new Date(entry.updated_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarViewDialog;